import {
  ActionExecutionEventType,
  ActionExecutionStatus,
  AutonomyState,
  CommunicationChannel,
  EscalationType,
  ExecutionMode,
  ToolCallStatus,
} from '@shared/generated/graphql';
import { ActionDefinitionPreview } from './action-definition';
import { Conversation } from './conversation';
import { Document } from './document';
import { User } from './user';

export type ActionExecutionDocument = Document & {
  createdAt: Date;
};

export type ActionExecution = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  status: ActionExecutionStatus;
  autonomyState: AutonomyState;
  executeAsUserId?: string;
  executeAsUser?: User;
  actionDefinitionPreview: ActionDefinitionPreview;
  children: ActionExecution[];
  conversation?: Conversation;
  linkedConversations: Conversation[];
  tags?: string[];
  mode: ExecutionMode;
  events: ActionExecutionEvent[];
  documents: ActionExecutionDocument[];
  input: any;
  output?: any;
  parentId?: string;
};

export type TaskListItem = {
  id: string;
  createdAt: Date;
  deletedAt: Date | null;
  status: ActionExecutionStatus;
  autonomyState: AutonomyState;
  tags: string[];
  actionDefinitionName: string;
};

export type ActionExecutionEvent = {
  id: string;
  createdAt: Date;
  name: ActionExecutionEventType;
  agentMessageView?: AgentMessageView | null;
  data?: ActionExecutionEventData;
};

type EscalationEventData = {
  eventDataType: ActionExecutionEventType.ESCALATION;
  escalationType: EscalationType;
  message: string;
  escalationRationale?: string;
  codeExecutionError?: string | null;
};

type DecisionEdgeEvaluatedEventData = {
  eventDataType: ActionExecutionEventType.DECISION_EDGE_EVALUATED;
  rationale: string;
  shouldFollowEdge: boolean;
  targetNodeActionDefinitionId?: string;
};

export type ToolEventMetadata = Record<string, unknown> & {
  childActionExecutionId?: string | null;
  previousComment?: string | null;
};

type ToolUsedCommonData = {
  toolName: string;
  toolInput: object;
  toolOutput?: object | string | null;
  rawMessage?: JSON;
  metadata?: ToolEventMetadata | null;
  status?: ToolCallStatus | null;
};

export type ToolUsedEventData = ToolUsedCommonData & {
  eventDataType: ActionExecutionEventType.TOOL_USED;
};

export type SupervisorToolUsedEventData = ToolUsedCommonData & {
  eventDataType: ActionExecutionEventType.SUPERVISOR_TOOL_USED;
};

export type AgentMessageToolCall = {
  id?: string | null;
  name: string;
  args?: JSON | null;
};

export type SentMessageView = {
  subject?: string | null;
  body: string;
  toRecipients: string[];
  ccRecipients: string[];
};

export type AgentMessageView = {
  content: string;
  toolCalls: AgentMessageToolCall[];
  sentMessage?: SentMessageView | null;
};

export type RawAgentMessageEventData = {
  eventDataType: ActionExecutionEventType.RAW_AGENT_MESSAGE;
  data: JSON;
};

export type RawSupervisorMessageEventData = {
  eventDataType: ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE;
  data: JSON;
};

export type AgentReasoningEventData = {
  eventDataType: ActionExecutionEventType.AGENT_REASONING;
  reasoning: string;
};

export type SupervisorReasoningEventData = {
  eventDataType: ActionExecutionEventType.SUPERVISOR_REASONING;
  reasoning: string;
};

export type MemoryRecalledEventData = {
  eventDataType: ActionExecutionEventType.MEMORY_RECALLED;
  memories: { id: string; comment: string }[];
};

export type SpamGuardEventData = {
  eventDataType: ActionExecutionEventType.SPAM_GUARD;
  communicationChannel: CommunicationChannel;
  fromIdentifier: string;
  toIdentifier: string;
  reason: string;
};

export type ReminderEventData = {
  eventDataType: ActionExecutionEventType.REMINDER;
  message: string;
};

export type InstructionMessageEventData = {
  eventDataType: ActionExecutionEventType.INSTRUCTION_MESSAGE;
  message: string;
  sourceActionExecutionId?: string | null;
};

export type StatusChangedEventData = {
  eventDataType: ActionExecutionEventType.STATUS_CHANGED;
  fromStatus: ActionExecutionStatus;
  toStatus: ActionExecutionStatus;
};

export type AgentProposedOutputEventData = {
  eventDataType: ActionExecutionEventType.AGENT_PROPOSED_OUTPUT;
  output: JSON | string;
};

export type SupervisorProposedOutputEventData = {
  eventDataType: ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT;
  output: JSON | string;
};

type UnknownEventData = {
  eventDataType: ActionExecutionEventType.UNKNOWN;
};

export type WatcherClosedEventData = {
  eventDataType: ActionExecutionEventType.WATCHER_CLOSED;
};

export type WatcherPokedEventData = {
  eventDataType: ActionExecutionEventType.WATCHER_POKED;
};

export type ActionExecutionEventData =
  | EscalationEventData
  | DecisionEdgeEvaluatedEventData
  | ToolUsedEventData
  | SupervisorToolUsedEventData
  | RawAgentMessageEventData
  | RawSupervisorMessageEventData
  | AgentReasoningEventData
  | SupervisorReasoningEventData
  | MemoryRecalledEventData
  | SpamGuardEventData
  | ReminderEventData
  | InstructionMessageEventData
  | StatusChangedEventData
  | AgentProposedOutputEventData
  | SupervisorProposedOutputEventData
  | WatcherClosedEventData
  | WatcherPokedEventData
  | UnknownEventData;

export function isRunningToolCall(data?: ActionExecutionEventData): boolean {
  return (
    (data?.eventDataType === ActionExecutionEventType.TOOL_USED ||
      data?.eventDataType === ActionExecutionEventType.SUPERVISOR_TOOL_USED) &&
    data.status === ToolCallStatus.IN_PROGRESS
  );
}
